<template>
  <div class="page-container">
    <h2 class="page-title">商品分类管理</h2>

    <!-- 搜索栏 -->
    <div class="search-bar">
      <el-input v-model="searchKey" placeholder="请输入分类名称" style="width: 300px" clearable @keyup.enter="handleSearch" />
      <el-button type="primary" @click="handleSearch">查询</el-button>
      <el-button @click="resetSearch">重置</el-button>
      <el-button type="success" @click="openAddDialog">新增分类</el-button>
    </div>

    <!-- 数据表格 -->
    <el-table :data="groupList" border v-loading="loading">
      <el-table-column prop="id" label="ID" width="80" />
      <el-table-column prop="name" label="分类名称" />
      <el-table-column label="操作" width="200" fixed="right">
        <template #default="{ row }">
          <el-button type="primary" size="small" @click="openEditDialog(row)">编辑</el-button>
          <el-popconfirm title="确定删除该分类？" @confirm="handleDelete(row.id)">
            <template #reference>
              <el-button type="danger" size="small">删除</el-button>
            </template>
          </el-popconfirm>
        </template>
      </el-table-column>
    </el-table>

    <!-- 分页 -->
    <div class="pagination">
      <el-pagination v-model:current-page="pagination.page" v-model:page-size="pagination.limit"
        :total="pagination.total" :page-sizes="[10, 20, 50]" layout="total, sizes, prev, pager, next, jumper"
        @size-change="fetchData" @current-change="fetchData" />
    </div>

    <!-- 新增/编辑弹窗 -->
    <el-dialog v-model="dialogVisible" :title="isEditMode ? '编辑分类' : '新增分类'" width="500px">
      <el-form :model="formData" label-width="80px">
        <el-form-item label="分类名称" prop="name" required>
          <el-input v-model="formData.name" />
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="dialogVisible = false">取消</el-button>
        <el-button type="primary" @click="submitForm">确定</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted } from 'vue'
import { ElMessage } from 'element-plus'
import Cookies from 'js-cookie'
import config from '@/config'
import axios from 'axios'

const userInfo = JSON.parse(Cookies.get('user_info') || '{}')
const storeId = userInfo.id

// 数据相关
const loading = ref(false)
const searchKey = ref('')
const dialogVisible = ref(false)
const isEditMode = ref(false)
const groupList = ref([])
const formData = reactive({
  id: null,
  name: ''
})

// 分页配置
const pagination = reactive({
  page: 1,
  limit: 10,
  total: 0
})

// 加载数据
const fetchData = async () => {
  try {
    loading.value = true
    const params = {
      store_id: storeId,
      page: pagination.page,
      limit: pagination.limit,
      search: searchKey.value
    }

    const { data } = await axios.get(`${config.BASE_URL}/shop/api/grouping_api.php`, { params })

    if (data.code === 200) {
      groupList.value = data.data.list
      pagination.total = data.data.pagination.total
    }
  } catch (error) {
    ElMessage.error('数据加载失败')
  } finally {
    loading.value = false
  }
}

// 搜索处理
const handleSearch = () => {
  pagination.page = 1
  fetchData()
}

// 重置搜索
const resetSearch = () => {
  searchKey.value = ''
  fetchData()
}

// 打开弹窗
const openAddDialog = () => {
  isEditMode.value = false
  formData.id = null
  formData.name = ''
  dialogVisible.value = true
}

// 打开编辑
const openEditDialog = (row) => {
  isEditMode.value = true
  formData.id = row.id
  formData.name = row.name
  dialogVisible.value = true
}

// 提交表单
const submitForm = async () => {
  try {
    const url = `${config.BASE_URL}/shop/api/grouping_api.php`
    const method = isEditMode.value ? 'put' : 'post'
    const payload = {
      ...formData,
      store_id: storeId
    }

    await axios[method](url, payload)
    ElMessage.success('操作成功')
    dialogVisible.value = false
    fetchData()
  } catch (error) {
    // 修改这里：显示完整的错误详情
    let errorMessage = error.message;

    // 如果是axios错误，尝试获取更详细的错误信息
    if (error.response) {
      errorMessage = error.response.data?.message || error.response.statusText;
    } else if (error.request) {
      errorMessage = '请求未收到响应';
    }

    // 显示错误提示（持续时间设为5秒，让用户有足够时间阅读）
    ElMessage.error({
      message: `操作失败: ${errorMessage}`,
      duration: 5000, // 5秒
      showClose: true // 显示关闭按钮
    });
  }
}

// 删除处理
const handleDelete = async (id) => {
  try {
    await axios.delete(`${config.BASE_URL}/shop/api/grouping_api.php`, {
      data: { id, store_id: storeId }
    })
    ElMessage.success('删除成功')
    fetchData()
  } catch (error) {
    ElMessage.error('删除失败')
  }
}

// 初始化加载
onMounted(() => {
  fetchData()
})
</script>

<style scoped>
.page-title {
  margin-bottom: 20px;
  color: #333;
}

.search-bar {
  margin-bottom: 20px;
  display: flex;
  gap: 10px;
}

.pagination {
  margin-top: 20px;
  display: flex;
  justify-content: flex-end;
}
</style>